import React, {useContext, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../../providers/authProvider";
import {ApiContext} from "../../contexts/ApiContext";
import DefaultLayoutWithoutSideBar from "../../../layouts/DefaultLayoutWithoutSideBar";
import BoxLayout from "../../../layouts/BoxLayout";
import H1Header from "../../headers/h1";
import DefaultButton from "../../buttons/DefaultButton";

const FINAL_STATUSES = ["paid", "failed", "canceled", "expired"];

function PaymentReturnPage() {
    const {Url} = useContext(ApiContext);
    const {token} = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [status, setStatus] = useState<string>("");
    const [payment, setPayment] = useState<any>(null);
    const [message, setMessage] = useState("Bezig met het ophalen van je betaling...");
    const pollRef = useRef<any>(null);

    // Resolve the payment id: prefer the query param, otherwise use the newest payment.
    async function resolvePaymentId(): Promise<number | null> {
        const idParam = searchParams.get("id");
        if (idParam) {
            return Number(idParam);
        }
        const res = await fetch(Url + "user/payments", {
            method: "GET",
            headers: {
                "content-type": "application/json",
                "Authorization": "Bearer " + token,
            },
        });
        if (res.status !== 200) {
            return null;
        }
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
            return Number(data[0].ID);
        }
        return null;
    }

    async function fetchStatus(id: number) {
        const res = await fetch(Url + "user/payment-status/" + id, {
            method: "GET",
            headers: {
                "content-type": "application/json",
                "Authorization": "Bearer " + token,
            },
        });
        if (res.status !== 200) {
            return null;
        }
        return res.json();
    }

    useEffect(() => {
        if (!token) {
            return;
        }
        let cancelled = false;

        async function start() {
            const id = await resolvePaymentId();
            if (cancelled) {
                return;
            }
            if (!id) {
                setMessage("We konden je betaling niet vinden. Bekijk je betalingen in het overzicht.");
                return;
            }

            const poll = async () => {
                const data = await fetchStatus(id);
                if (cancelled || !data) {
                    return;
                }
                setPayment(data);
                setStatus(data.Status);
                if (FINAL_STATUSES.includes(data.Status)) {
                    if (pollRef.current) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                    }
                }
            };

            await poll();
            pollRef.current = setInterval(poll, 2500);
        }

        start();

        return () => {
            cancelled = true;
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, Url]);

    let content;
    if (status === "paid") {
        content = (
            <p className="text-base leading-relaxed text-green-600">
                Je betaling is gelukt. Bedankt! De factuur is verwerkt.
            </p>
        );
    } else if (status === "failed" || status === "canceled" || status === "expired") {
        content = (
            <p className="text-base leading-relaxed text-red-500">
                De betaling is niet voltooid ({status}). Je kunt het opnieuw proberen vanuit je overzicht.
            </p>
        );
    } else if (status === "open" || status === "pending") {
        content = (
            <p className="text-base leading-relaxed">
                We wachten op de bevestiging van je betaling. Dit venster ververst automatisch...
            </p>
        );
    } else {
        content = (
            <p className="text-base leading-relaxed">{message}</p>
        );
    }

    return (
        <DefaultLayoutWithoutSideBar>
            <div className="flex flex-col py-4 sm:py-0">
                <BoxLayout>
                    <div className="mb-8">
                        <H1Header headerText="Status van je betaling"/>
                    </div>
                    {payment && (
                        <div className="mb-4">
                            {payment.Description && <p>Omschrijving: {payment.Description}</p>}
                            {payment.Amount && <p>Bedrag: {payment.Amount} {payment.Currency}</p>}
                        </div>
                    )}
                    {content}
                    <div className="mt-8">
                        <DefaultButton content="Terug naar overzicht"
                                       onClick={() => navigate("/user/visits-overview", {replace: false})}/>
                    </div>
                </BoxLayout>
            </div>
        </DefaultLayoutWithoutSideBar>
    );
}

export default PaymentReturnPage;
